import React from 'react';
import { motion } from 'motion/react';
import { PublicHeader } from '../components/shell/PublicHeader';
import { PublicFooter } from '../components/shell/PublicFooter';
import { SEO } from '../components/SEO';
import { cn } from '../lib/utils';
import { Link } from 'react-router-dom';
import { 
  Network, 
  MessageSquare, 
  CreditCard, 
  ShieldCheck, 
  Sparkles, 
  CheckCircle,
  HelpCircle,
  TrendingUp,
  Award,
  ArrowRight
} from 'lucide-react';
import { Badge, Button } from '../components/ui';
import { useNavigate } from 'react-router-dom';

export function FeaturesPage() {
  const navigate = useNavigate();

  const featuresList = [
    {
      id: "hierarchy",
      icon: <Network className="w-6 h-6 text-blue-600" />,
      title: "Sub-Chapters & Network Hierarchy",
      description: "Structure your organization with clear hierarchy. Scales from single chapters on the free tier up to multi-tier leagues, regions, and national networks with unlimited depth on Enterprise.",
      tier: "Scales with tier"
    },
    {
      id: "roles",
      icon: <CheckCircle className="w-6 h-6 text-indigo-600" />,
      title: "Role-Based Access for Chapters",
      description: "Create custom leadership titles like 'Treasurer' or 'Regional Director' with fine-grained permissions. Set them once at the top level to automatically apply to every group beneath.",
      tier: "Included in all tiers"
    },
    {
      id: "pulse",
      icon: <MessageSquare className="w-6 h-6 text-purple-600" />,
      title: "Pulse Notifications",
      description: "Send targeted announcements to exactly the right people. Reach a specific team, an entire region, or your whole network with critical alerts that cut through the noise.",
      tier: "Included in all tiers"
    },
    {
      id: "ledger",
      icon: <CreditCard className="w-6 h-6 text-emerald-600" />,
      title: "Ledger & Automatic Split Payouts",
      description: <>Collect dues, sell event tickets, and run fundraising campaigns via <Link to="/pricing" className="underline hover:text-black dark:hover:text-white transition-colors">Stripe integrations</Link>. When a local chapter collects funds, the platform automatically routes the agreed percentage to regional and national accounts.</>,
      schemaText: "Collect dues, sell event tickets, and run fundraising campaigns via Stripe integrations. When a local chapter collects funds, the platform automatically routes the agreed percentage to regional and national accounts.",
      tier: "Scales by tier"
    },
    {
      id: "vault",
      icon: <ShieldCheck className="w-6 h-6 text-rose-600" />,
      title: "Compliance & Digital Vault",
      description: <>Store sensitive documents securely. Keep bylaws, signed waivers, financial filings, and background checks in a <Link to="/vault" className="underline hover:text-black dark:hover:text-white transition-colors">secure digital vault</Link> with strict access controls based on organizational roles.</>,
      schemaText: "Store sensitive documents securely. Keep bylaws, signed waivers, financial filings, and background checks in a secure digital vault with strict access controls based on organizational roles.",
      tier: "Included in all tiers"
    },
    {
      id: "branding",
      icon: <Sparkles className="w-6 h-6 text-amber-600" />,
      title: "The Forge (Custom Branding)",
      description: "Make the platform your own by customizing colors, logos, and terminology. Rename features to match your community's language. Members become 'Scouts' and Chapters become 'Lodges'.",
      tier: "Foundation & above"
    },
    {
      id: "sponsors",
      icon: <Award className="w-6 h-6 text-orange-600" />,
      title: "Sponsor & Dependent Linking",
      description: "Link parents to their children's accounts so they receive event reminders, announcements, and progress updates. Keep youth data completely protected with strict access controls.",
      tier: "Included in all tiers"
    },
    {
      id: "events",
      icon: <TrendingUp className="w-6 h-6 text-teal-600" />,
      title: "Events & Resource Booking",
      description: "Schedule events across multiple groups, track RSVPs, monitor attendance, and let members reserve shared facilities like meeting rooms or equipment from one unified calendar.",
      tier: "Included in all tiers"
    },
    {
      id: "analytics",
      icon: <TrendingUp className="w-6 h-6 text-blue-500" />,
      title: "Global Analytics & Reporting",
      description: "Gain instant visibility into your entire network. Track membership growth, financial health, and engagement metrics across every chapter and region from a single dashboard.",
      tier: "Momentum & above"
    },
    {
      id: "forms",
      icon: <CheckCircle className="w-6 h-6 text-indigo-500" />,
      title: "Custom Forms & Waivers",
      description: "Build digital forms for registrations, surveys, and liability waivers. Collect electronic signatures seamlessly during onboarding and store them automatically in member profiles.",
      tier: "Included in all tiers"
    },
    {
      id: "communication",
      icon: <MessageSquare className="w-6 h-6 text-purple-500" />,
      title: "Emergency SMS Broadcasts",
      description: "Reach your members instantly during critical situations. Send urgent SMS text alerts to specific rosters or your entire organization with one click when time matters most.",
      tier: "Momentum & above (or Add-on)"
    },
    {
      id: "api",
      icon: <Network className="w-6 h-6 text-emerald-500" />,
      title: "Developer API & Webhooks",
      description: "Connect your existing tools to our platform. Sync data with external CRMs, automate workflows, and build custom integrations using our robust developer API and webhooks.",
      tier: "Enterprise"
    }
  ];

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": featuresList.map(f => ({
      "@type": "Question",
      "name": f.title,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": f.schemaText || (typeof f.description === 'string' ? f.description : '')
      }
    }))
  };

  return (
    <div className="min-h-screen transition-colors duration-300 bg-white text-black dark:bg-black dark:text-white">
      <SEO 
        title="Features | Chapter & League Management Software | OrgSets" 
        description="Explore our features. Manage dues, digital vaults, roles, and communications across multi-tier networks. Built for leagues, chapters, and associations."
        url="https://orgsets.com/features"
        schema={faqSchema}
      />
      <PublicHeader />

      <main className="pb-24">
        {/* Hero Section */}
        <section className="grid grid-cols-1 lg:grid-cols-2 border-b-[8px] border-black dark:border-white min-h-[50vh] bg-black text-white dark:bg-white dark:text-black mb-24">
          <div className="p-8 pt-28 md:p-16 md:pt-28 lg:p-12 lg:pt-28 xl:p-24 xl:pt-28 flex flex-col justify-center h-full border-b-[8px] lg:border-b-0 lg:border-r-[8px] border-black dark:border-white bg-white dark:bg-black text-black dark:text-white">
            <h1 className="text-5xl sm:text-6xl md:text-8xl lg:text-6xl xl:text-7xl 2xl:text-[100px] font-black uppercase tracking-tighter leading-[0.85] mb-8 break-words">
              Pure Utility. <br/> Infinite <br/> Scaling.
            </h1>
            <p className="text-xl md:text-2xl font-bold leading-snug mb-12">
              Deploy professional governance, finance, and communication tools across your entire network.
            </p>
          </div>
          <div className="p-8 md:p-16 flex flex-col justify-center items-center h-full relative overflow-hidden bg-brand-primary text-white">
            <div className="absolute top-0 right-0 w-64 h-64 bg-pink-500 rounded-bl-full border-b-[8px] border-l-[8px] border-black dark:border-white" />
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-brand-secondary rounded-tr-full border-t-[8px] border-r-[8px] border-black dark:border-white" />
            <div className="text-5xl md:text-7xl font-black uppercase tracking-widest text-center relative z-10 leading-[0.9]">
              Infinite <br/> <span className="text-brand-secondary text-black">Hierarchy</span>
            </div>
          </div>
        </section>

        <div className="max-w-6xl mx-auto px-6">

          {/* Features Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 border-t-[4px] border-l-[4px] border-black dark:border-white">
            {featuresList.map((feature, idx) => (
              <motion.div
                key={feature.title}
                id={feature.id}
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
                className={cn(
                  "p-8 flex flex-col justify-between transition-colors group rounded-none shadow-none border-b-[4px] border-r-[4px] border-black dark:border-white hover:bg-brand-primary hover:text-white dark:hover:bg-brand-primary dark:hover:text-white",
                  idx % 4 === 1 ? "bg-brand-secondary text-black dark:bg-brand-secondary dark:text-black" : "bg-white dark:bg-black text-black dark:text-white"
                )}
              >
                <div className="space-y-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-white dark:bg-black border-[2px] border-black dark:border-white rounded-none shadow-[2px_2px_0px_#000] dark:shadow-[2px_2px_0px_#fff]">
                    {feature.icon}
                  </div>
                  <h3 className="font-display uppercase tracking-tighter text-2xl font-black">{feature.title}</h3>
                  <p className="text-sm leading-relaxed font-bold group-hover:text-white">{feature.description}</p>
                </div>
                <div className="pt-6 mt-6 border-t border-current">
                  <span className="text-xs font-bold flex items-center gap-1 uppercase">
                    <CheckCircle className="w-3.5 h-3.5" /> {feature.tier}
                  </span>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Deep Dive: Organizations as Members */}
          <div className="mt-24 pt-24 border-t border-black dark:border-white border-t-[8px]">
            <div className="flex flex-col lg:flex-row gap-16 items-center">
              <div className="flex-1 space-y-8">
                <Badge variant="blue" className="pointer-events-none bg-brand-primary text-white border-[4px] border-black rounded-none shadow-[4px_4px_0px_#000] uppercase font-black">
                  <Network className="w-4 h-4 mr-1.5 inline" /> Core Architecture
                </Badge>
                <h2 className="text-4xl md:text-5xl font-black font-display tracking-tight text-black dark:text-white">
                  Unified Club & Chapter Management.
                </h2>
                <div className="space-y-6 text-lg leading-relaxed font-bold">
                  <p>
                    Most platforms treat individual users as the only possible members of a group. <strong>OrgSets completely rebuilds this concept.</strong>
                  </p>
                  <p>
                    In OrgSets, an organization can join another organization as a member, unlocking an unlimited sub-chapter structure. This means you can create a Global Headquarters that oversees National Regions, which oversee Local Chapters, which oversee individual Teams.
                  </p>
                  <ul className="space-y-4 text-base font-sans">
                    <li className="flex items-start gap-3">
                      <div className="mt-1 p-1 bg-black text-white">
                        <CheckCircle className="w-4 h-4 text-white" />
                      </div>
                      <span><strong>Set Rules at HQ. Apply Them Everywhere.</strong> Policies set at the Headquarters level automatically apply to every chapter in the network.</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="mt-1 p-1 bg-black text-white">
                        <CheckCircle className="w-4 h-4 text-white" />
                      </div>
                      <span><strong>Global Analytics:</strong> Directors get a unified dashboard rolling up analytics, finances, and activity from all organizations beneath them.</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <div className="mt-1 p-1 bg-black text-white">
                        <CheckCircle className="w-4 h-4 text-white" />
                      </div>
                      <span><strong>Independent Chapter Controls:</strong> Each sub-organization maintains its own ledger, members list, and announcements while still belonging to the network.</span>
                    </li>
                  </ul>
                </div>
              </div>
              <div className="flex-1 w-full p-8 relative bg-white dark:bg-black border-[4px] border-black dark:border-white rounded-none shadow-[12px_12px_0px_#000] dark:shadow-[12px_12px_0px_#fff]">
                 <div className="absolute top-4 right-4 text-xs font-mono text-gray-400">NETWORK_STRUCTURE_PREVIEW</div>
                 <div className="space-y-4 mt-4">
                    {/* Mock Hierarchy UI */}
                    <div className="p-4 flex items-center justify-between z-20 relative bg-white dark:bg-black border-[4px] border-black dark:border-white shadow-[4px_4px_0px_#000] rounded-none">
                       <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-blue-600 flex items-center justify-center text-white font-bold">HQ</div>
                          <div>
                            <div className="font-bold text-navy-900 dark:text-white">Global Federation</div>
                            <div className="text-xs text-gray-500">Global Network • 150k members</div>
                          </div>
                       </div>
                       <Badge variant="green">Verified</Badge>
                    </div>

                    <div className="pl-8 relative space-y-4">
                       <div className="absolute left-4 top-[-1rem] bottom-8 w-px bg-gray-300 dark:bg-slate-700"></div>
                       
                       <div className="bg-white dark:bg-slate-950 border border-gray-200 dark:border-slate-800 p-4 rounded-xl flex items-center gap-3 shadow-sm relative">
                          <div className="absolute left-[-2rem] top-1/2 w-8 h-px bg-gray-300 dark:bg-slate-700"></div>
                          <div className="w-8 h-8 rounded-lg bg-purple-600 flex items-center justify-center text-white font-bold text-xs">NA</div>
                          <div>
                            <div className="font-bold text-navy-900 dark:text-white text-sm">North America Region</div>
                            <div className="text-xs text-gray-500">Regional Chapter • 45 Chapters</div>
                          </div>
                       </div>

                       <div className="bg-white dark:bg-slate-950 border border-gray-200 dark:border-slate-800 p-4 rounded-xl flex items-center gap-3 shadow-sm relative">
                          <div className="absolute left-[-2rem] top-1/2 w-8 h-px bg-gray-300 dark:bg-slate-700"></div>
                          <div className="w-8 h-8 rounded-lg bg-emerald-600 flex items-center justify-center text-white font-bold text-xs">EU</div>
                          <div>
                            <div className="font-bold text-navy-900 dark:text-white text-sm">European Union Region</div>
                            <div className="text-xs text-gray-500">Regional Chapter • 28 Chapters</div>
                          </div>
                          <div className="ml-auto w-5 h-5 rounded-full border border-gray-300 dark:border-slate-600 flex items-center justify-center text-gray-400 cursor-pointer hover:bg-gray-100 dark:hover:bg-slate-800">+</div>
                       </div>
                       
                       {/* Third level */}
                       <div className="pl-8 relative space-y-4 mt-4">
                           <div className="absolute left-4 top-[-1rem] bottom-8 w-px bg-gray-200 dark:bg-slate-800"></div>
                           <div className="bg-gray-50 dark:bg-slate-900 border border-gray-200 dark:border-slate-800 p-3 rounded-xl flex items-center gap-3 shadow-sm relative opacity-80">
                              <div className="absolute left-[-2rem] top-1/2 w-8 h-px bg-gray-200 dark:bg-slate-800"></div>
                              <div className="w-6 h-6 rounded bg-rose-600 flex items-center justify-center text-white font-bold text-[10px]">UK</div>
                              <div>
                                <div className="font-bold text-navy-900 dark:text-white text-xs">London Chapter</div>
                                <div className="text-[10px] text-gray-500">Local Branch • 200 members</div>
                              </div>
                           </div>
                       </div>
                    </div>
                 </div>
              </div>
            </div>
          </div>

          {/* Value Props Showcase */}
          <div className="mt-32 p-8 md:p-16 relative overflow-hidden bg-brand-primary text-white border-[8px] border-black dark:border-white shadow-[16px_16px_0px_#000] dark:shadow-[16px_16px_0px_#fff] rounded-none">
            <div className="grid md:grid-cols-2 gap-12 items-center relative z-10">
              <div className="space-y-6">
                <Badge variant="blue" className="bg-white text-black border-[4px] border-black rounded-none shadow-[4px_4px_0px_#000] font-black uppercase tracking-widest">Unified Command</Badge>
                <h2 className="text-3xl md:text-5xl font-black font-display tracking-tight leading-tight text-white">
                  Run your entire organization from one place.
                </h2>
                <p className="leading-relaxed text-base font-bold text-white font-sans">
                  OrgSets bridges the gap between chaotic group spaces and rigid enterprise software, bringing professional coordination tools to teams of any size.
                </p>
                <div className="flex flex-wrap gap-4 pt-2">
                  <Button onClick={() => navigate('/sign-up')} className="px-6 py-3 font-bold cursor-pointer bg-white dark:bg-black text-black dark:text-white border-[4px] border-black dark:border-white shadow-[8px_8px_0px_#000] rounded-none hover:translate-x-[-4px] hover:translate-y-[-4px] hover:shadow-[12px_12px_0px_#000]">
                    INITIALIZE ORG
                  </Button>
                </div>
              </div>

              <div className="space-y-4 font-mono text-xs text-white">
                <div className="flex items-center gap-3 p-4 bg-black/10 border-[4px] border-black dark:border-white shadow-[4px_4px_0px_#000] rounded-none">
                  <TrendingUp className="w-5 h-5 text-current" />
                  <div>
                    <h4 className="font-bold text-current text-sm">99% Less Interruption</h4>
                    <p className="opacity-80 text-[11px] mt-0.5 font-sans">Filter announcements directly into targeted groups to prevent notification overload.</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-4 bg-black/10 border-[4px] border-black dark:border-white shadow-[4px_4px_0px_#000] rounded-none">
                  <Award className="w-5 h-5 text-current" />
                  <div>
                    <h4 className="font-bold text-current text-sm">Secure Audit-Ready Ledgers</h4>
                    <p className="opacity-80 text-[11px] mt-0.5 font-sans">Track every fee, payment, and transaction globally with automatic administrative validation.</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-4 bg-black/10 border-[4px] border-black dark:border-white shadow-[4px_4px_0px_#000] rounded-none">
                  <HelpCircle className="w-5 h-5 text-current" />
                  <div>
                    <h4 className="font-bold text-current text-sm">Independent Chapter Controls</h4>
                    <p className="opacity-80 text-[11px] mt-0.5 font-sans">Give each chapter the tools to manage their own events, members, and finances without needing HQ approval.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* HQ Pitch Generator Callout */}
          <div className="mt-20 p-8 flex flex-col md:flex-row items-center justify-between gap-8 text-left border transition-all hover:shadow-lg bg-white dark:bg-black border-[4px] border-black dark:border-white shadow-[12px_12px_0px_#000] dark:shadow-[12px_12px_0px_#fff] rounded-none">
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Sparkles size={18} className="text-brand-primary" />
                <span className="text-sm font-bold uppercase tracking-widest text-brand-primary">Grassroots Growth</span>
              </div>
              <h3 className="text-2xl font-black font-display mb-2 text-black dark:text-white">Part of a larger network?</h3>
              <p className="text-base max-w-lg font-bold">
                Are you a local chapter or club? Generate a custom proposal to pitch OrgSets to your National or Regional Headquarters and unlock platform-wide tools.
              </p>
            </div>
            <div className="shrink-0 w-full md:w-auto">
              <Link 
                to="/tools/hq-pitch-generator"
                className="w-full md:w-auto inline-flex items-center justify-center gap-2 group whitespace-nowrap font-mono font-bold uppercase tracking-wider text-xs px-6 py-3 border-2 border-brand-primary/20 hover:border-brand-primary/50 bg-white/50 dark:bg-black/50 backdrop-blur-md text-black dark:text-white transition-all duration-150"
              >
                Generate Executive HQ Pitch <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>
          {/* Bottom CTA */}
          <section className="mt-32 p-12 text-center border-t bg-white text-black dark:bg-black dark:text-white border-t-[8px] border-black dark:border-white">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-4xl md:text-6xl font-black mb-8 tracking-tighter">
                Empower your local chapters today.
              </h2>
              <p className="text-xl mb-10 font-bold">
                Bring clarity and organization to your entire network.
              </p>
              <div className="flex flex-col justify-center items-center gap-2">
                 <Button size="lg" className="px-12 py-6 text-xl font-black group bg-black text-white dark:bg-white dark:text-black border-[4px] border-black dark:border-white shadow-[8px_8px_0px_#000] dark:shadow-[8px_8px_0px_#fff] rounded-none hover:translate-x-[-4px] hover:translate-y-[-4px] hover:shadow-[12px_12px_0px_#000] dark:hover:shadow-[12px_12px_0px_#fff] uppercase tracking-widest" onClick={() => navigate('/sign-up')}>
                    CREATE FREE ORGANIZATION
                 </Button>
                 <p className="text-sm font-bold opacity-80 mt-2">
                   Setup in 60 seconds. Need board approval? <Link to="/board-memo" className="underline hover:text-[#0044FF] dark:hover:text-[#FFD600]">View 1-Page Board Pitch Memo</Link>
                 </p>
              </div>
            </div>
          </section>

        </div>
      </main>

      <PublicFooter />
    </div>
  );
}
